import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { useAppServices } from "../../../hook/services";
import { toast } from "react-toastify";
import {
  Gear,
  Info,
  Lightning,
  CheckCircle,
  Phone,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { useAppInfo } from "../../../context/app";
import LoadingScreen from "../../../components/LoadingScreen";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "3rem",
    borderRadius: "0.65rem",
    borderColor: state.isFocused ? "#2563eb" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(37,99,235,0.15)" : "none",
    "&:hover": { borderColor: "#2563eb" },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#2563eb"
      : state.isFocused
      ? "#eff6ff"
      : "white",
    color: state.isSelected ? "white" : "#111827",
    cursor: "pointer",
  }),
  menu: (base) => ({ ...base, borderRadius: "0.65rem", overflow: "hidden" }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const Configuration = () => {
  const { appId, locationId, companyId } = useParams();
  const AppService = useAppServices();
  const [app] = useAppInfo();
  const [appInstall, setAppInstall] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);
  const [numberSearch, setNumberSearch] = useState("");

  const expectedWebhookUrl = `https://connect.myhook.site/webhook/message_events/${locationId}/${appId}`;

  const fetchPhoneNumbers = async () => {
    setPhoneNumbersLoading(true);
    const res = await AppService.twilio.get_phone_numbers({
      appId,
      query: `locationId=${locationId}&companyId=${companyId}`,
    });
    if (res?.response) setPhoneNumbers(res.response.data || []);
    setPhoneNumbersLoading(false);
  };

  const previewPhoneNumbers = async (accId, token) => {
    if (!accId || !token) return;
    setPhoneNumbersLoading(true);
    const res = await AppService.twilio.preview_phone_numbers({
      appId,
      payload: {
        extras: { locationId, companyId },
        data: { acc_id: accId, token },
      },
    });
    if (res?.response?.success) setPhoneNumbers(res.response.data || []);
    setPhoneNumbersLoading(false);
  };

  const handleCredentialBlur = () => {
    if (formData.acc_id && formData.token) {
      previewPhoneNumbers(formData.acc_id, formData.token);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const installRes = await AppService.app_installs.filter({
        query: `appId=${appId}&locationId=${locationId}`,
      });
      if (installRes?.response) {
        setAppInstall(installRes.response.data);
        setFormData(installRes.response.data?.credentials || {});

        if (
          installRes.response.data?.credentials?.acc_id &&
          installRes.response.data?.credentials?.token
        ) {
          fetchPhoneNumbers();
        }
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handlePhoneSelect = (value) =>
    setFormData({ ...formData, from_number: value || "" });

  const ensureWebhook = async () => {
    setWebhookLoading(true);
    const res = await AppService.twilio.ensure_webhook({
      appId,
      payload: { extras: { locationId, companyId } },
    });
    setWebhookLoading(false);
    if (res?.response?.success) {
      const { alreadyConfigured } = res.response.data || {};
      toast.success(
        alreadyConfigured ? "Webhook already configured!" : "Webhook configured!"
      );
      fetchPhoneNumbers();
    } else {
      toast.error(res?.response?.message || "Failed to configure the webhook.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      _id: appInstall?._id,
      credentials: formData,
    };
    const { response } = await AppService.app_installs.update({ payload });
    if (response) {
      toast.success("Configuration saved!");
      if (formData.acc_id && formData.token) fetchPhoneNumbers();
    }
    setSaving(false);
  };

  if (loading) {
    return <LoadingScreen loadingText={"Loading data..."} />;
  }

  const phoneOptions = phoneNumbers.map((num) => ({
    value: num.value,
    label: num.label,
  }));
  const selectedPhoneOption =
    phoneOptions.find((o) => o.value === formData.from_number) || null;
  const filteredPhoneNumbers = phoneNumbers.filter((num) => {
    const query = numberSearch.trim().toLowerCase();
    if (!query) return true;
    return (
      num.name?.toLowerCase().includes(query) ||
      num.number?.toLowerCase().includes(query)
    );
  });
  const selectedNumberRecord = phoneNumbers.find(
    (num) => num.value === formData.from_number
  );
  const webhookConnected = selectedNumberRecord?.smsUrl === expectedWebhookUrl;

  return (
    <div className="py-5 bg-light min-vh-100">
      <div className="container-fluid px-4 px-xl-5">
        <div className="row g-4">
          {/* Credentials */}
          <div className="col-lg-5 col-xl-4">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body" style={{ padding: "2rem" }}>
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                    style={{
                      width: 44,
                      height: 44,
                      background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                      color: "white",
                    }}
                  >
                    <Gear size={20} weight="bold" />
                  </div>
                  <div>
                    <div className="fw-semibold">Configuration</div>
                    <div className="text-muted small">
                      Connect your Twilio account to start sending messages.
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  {[...(app.formFields || [])].map((field, i) => {
                    const isNumberField = field.key === "from_number";
                    return (
                      <div className="mb-3" key={i}>
                        <label
                          htmlFor={field.key}
                          className="form-label fw-medium small text-dark d-flex align-items-center"
                        >
                          {field.label}
                          {field.short_description && (
                            <span title={field.short_description}>
                              <Info
                                size={14}
                                weight="bold"
                                className="ms-1 text-secondary"
                                style={{ cursor: "help" }}
                              />
                            </span>
                          )}
                          {field.key === "token" && phoneNumbersLoading && (
                            <span
                              className="spinner-border spinner-border-sm text-primary ms-2"
                              role="status"
                              aria-hidden="true"
                            />
                          )}
                        </label>

                        {isNumberField ? (
                          <Select
                            inputId={field.key}
                            options={phoneOptions}
                            value={selectedPhoneOption}
                            onChange={(opt) => handlePhoneSelect(opt?.value)}
                            isLoading={phoneNumbersLoading}
                            isDisabled={
                              !formData.acc_id || !formData.token
                            }
                            placeholder={
                              !formData.acc_id || !formData.token
                                ? "Enter your Account SID and Token first"
                                : "Search a phone number…"
                            }
                            noOptionsMessage={() =>
                              phoneNumbersLoading
                                ? "Loading numbers…"
                                : "No numbers found on this account"
                            }
                            isSearchable
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                          />
                        ) : (
                          <input
                            type="text"
                            id={field.key}
                            name={field.key}
                            value={formData[field.key] || ""}
                            onChange={handleChange}
                            onBlur={
                              field.key === "acc_id" || field.key === "token"
                                ? handleCredentialBlur
                                : undefined
                            }
                            placeholder={`Enter ${field.label}`}
                            className="form-control rounded-3"
                            style={{ height: "3rem" }}
                          />
                        )}

                        {isNumberField && phoneNumbers.length === 0 && (
                          <small className="text-muted">
                            {phoneNumbersLoading
                              ? "Loading available numbers…"
                              : "Enter your Account SID and Auth Token above to load your available numbers."}
                          </small>
                        )}
                      </div>
                    );
                  })}

                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary w-100 mt-3 fw-semibold rounded-3"
                    style={{ padding: "0.85rem" }}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving…
                      </>
                    ) : (
                      "Save Configuration"
                    )}
                  </button>
                </form>

                {formData.acc_id && formData.token && formData.from_number && (
                  <>
                    <hr className="my-4" />
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <Lightning size={16} weight="bold" className="text-warning" />
                      <span className="fw-semibold small">Webhook</span>
                    </div>

                    {webhookLoading ? (
                      <div className="small p-2 border rounded-3 bg-light text-muted d-flex align-items-center gap-2">
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Configuring webhook…
                      </div>
                    ) : webhookConnected ? (
                      <div className="small p-2 border rounded-3 bg-light d-flex align-items-center justify-content-between gap-2">
                        <span className="d-flex align-items-center gap-2 text-success">
                          <CheckCircle size={16} weight="fill" />
                          Webhook connected
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={ensureWebhook}
                        >
                          Reconfigure
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary w-100"
                        onClick={ensureWebhook}
                      >
                        Configure Webhook
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Numbers on this account */}
          <div className="col-lg-7 col-xl-8">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body" style={{ padding: "2rem" }}>
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <div className="fw-semibold">Numbers on this account</div>
                    <div className="text-muted small">
                      Click a number to set it as your default sender.
                    </div>
                  </div>
                  {phoneNumbers.length > 0 && (
                    <span className="badge rounded-pill text-bg-light border text-secondary">
                      {phoneNumbers.length} number
                      {phoneNumbers.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>

                {phoneNumbers.length > 0 && (
                  <div className="input-group mb-3" style={{ maxWidth: 320 }}>
                    <span className="input-group-text bg-white border-end-0">
                      <MagnifyingGlass size={16} className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search numbers…"
                      value={numberSearch}
                      onChange={(e) => setNumberSearch(e.target.value)}
                    />
                  </div>
                )}

                {phoneNumbers.length > 0 ? (
                  filteredPhoneNumbers.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead>
                          <tr className="text-muted small text-uppercase">
                            <th className="fw-semibold border-0" style={{ letterSpacing: "0.03em" }}>
                              Number
                            </th>
                            <th className="fw-semibold border-0" style={{ letterSpacing: "0.03em" }}>
                              Status
                            </th>
                            <th className="fw-semibold border-0" style={{ letterSpacing: "0.03em" }}>
                              Webhook
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPhoneNumbers.map((num) => {
                            const isDefault = formData.from_number === num.value;
                            const hasWebhook = num.smsUrl === expectedWebhookUrl;
                            return (
                              <tr key={num.value}>
                                <td>
                                  <button
                                    type="button"
                                    onClick={() => handlePhoneSelect(num.value)}
                                    className="btn p-0 border-0 bg-transparent text-start d-flex align-items-center gap-3"
                                  >
                                    <div
                                      className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                      style={{
                                        width: 36,
                                        height: 36,
                                        background: isDefault ? "#2563eb" : "#f1f3f5",
                                        color: isDefault ? "white" : "#6c757d",
                                      }}
                                    >
                                      <Phone size={16} weight="bold" />
                                    </div>
                                    <div>
                                      <div className="fw-medium small">
                                        {num.name || "Unnamed number"}
                                      </div>
                                      <div className="text-muted small">{num.number}</div>
                                    </div>
                                  </button>
                                </td>
                                <td>
                                  {isDefault && (
                                    <span className="badge rounded-pill text-bg-primary d-flex align-items-center gap-1" style={{ width: "fit-content" }}>
                                      <CheckCircle size={12} weight="fill" />
                                      Default
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {hasWebhook ? (
                                    <span className="badge rounded-pill text-bg-success-subtle text-success d-flex align-items-center gap-1" style={{ width: "fit-content" }}>
                                      <CheckCircle size={12} weight="fill" />
                                      Connected
                                    </span>
                                  ) : (
                                    <span className="text-muted small">Not configured</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted small py-4">
                      No numbers match "{numberSearch}".
                    </div>
                  )
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{ width: 56, height: 56, background: "#f1f3f5", color: "#6c757d" }}
                    >
                      <Phone size={24} weight="bold" />
                    </div>
                    <div className="fw-medium">
                      {phoneNumbersLoading ? "Loading your numbers…" : "No numbers loaded yet"}
                    </div>
                    <div className="text-muted small" style={{ maxWidth: 320 }}>
                      {phoneNumbersLoading
                        ? "This will only take a moment."
                        : "Enter your Account SID and Auth Token on the left to see every number on your account here."}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configuration;
